// Public CMS snapshots only. Do not reuse the admin draft feed or persist news
// bodies: an unpublished entry must disappear on the next successful read.
export const UPDATES_PATH = '/api/blog/updates/posts?limit=6&offset=0';
export const UPDATES_TIMEOUT_MS = 8000;

export function updateTimestamp(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:T.*(?:Z|[+-]\d{2}:\d{2}))?$/.test(value)) return null;
  const day = value.slice(0, 10);
  const calendar = new Date(`${day}T00:00:00Z`);
  const time = Date.parse(value);
  return Number.isFinite(time) && Number.isFinite(calendar.getTime()) && calendar.toISOString().slice(0, 10) === day ? time : null;
}

export function newsSite(baseURL) {
  try {
    const host = new URL(baseURL).hostname;
    if (host === 'api.dathent.ai') return 'https://dathent.ai';
    if (host === 'api.staging.dathent.ai') return 'https://app-lending-staging.pages.dev';
    if (host === 'api.dev.dathent.ai') return 'https://app-lending-dev.pages.dev';
    if (/^api\.\d+-ci\.dev\.dathent\.ai$/.test(host)) return `https://${host.slice(4)}`;
  } catch { /* An unknown local/preview API must not link to production. */ }
  return null;
}

export async function fetchPublishedUpdates(baseURL, signal) {
  const response = await fetch(new URL(UPDATES_PATH, baseURL).href, {
    method: 'GET', signal, credentials: 'omit', cache: 'no-store',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error('Updates unavailable');
  const payload = await response.json();
  if (payload?.ok !== true || !Array.isArray(payload.data?.posts)) throw new Error('Invalid updates response');
  const posts = payload.data.posts.flatMap(post => {
    const published = updateTimestamp(post?.publishedAt);
    if (!post || (post.status && post.status !== 'published') || published === null ||
      typeof post.title !== 'string' || !post.title.trim() || typeof post.slug !== 'string' ||
      post.slug.length > 120 || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(post.slug)) return [];
    const updated = updateTimestamp(post.updatedAt);
    const revision = Math.max(published, updated ?? published);
    return [{
      slug: post.slug, title: post.title.trim(), excerpt: typeof post.excerpt === 'string' ? post.excerpt : '',
      type: ['news', 'offer'].includes(post.type) ? post.type : 'update',
      revision, dateTime: new Date(revision).toISOString(),
      expiresAt: post.type === 'offer' ? updateTimestamp(post.offer?.expiresAt) : null,
      offerExpired: post.type === 'offer' && post.offer?.expired === true,
    }];
  });
  if (payload.data.posts.length && !posts.length) throw new Error('Invalid updates response');
  return posts.sort((a, b) => b.revision - a.revision);
}

export function updatesSeenKey(userId) {
  return `dathent.product-updates.seen.v1.${userId}`;
}

export function readUpdatesSeen(userId) {
  try {
    const value = Number(window.localStorage.getItem(updatesSeenKey(userId)));
    return Number.isFinite(value) && value > 0 ? value : 0;
  }
  catch { return 0; }
}

export function formatUpdateDate(timestamp, locale, compact = false) {
  const options = { day: 'numeric', month: 'short', ...(compact ? {} : { year: 'numeric' }), timeZone: 'UTC' };
  try { return new Intl.DateTimeFormat(locale, options).format(timestamp); }
  catch { return new Intl.DateTimeFormat('en', options).format(timestamp); }
}
